import { Link } from "react-router-dom";
import { ArrowUpRight, Stack, Cloud, Cpu, Broadcast, MonitorPlay } from "@phosphor-icons/react";

const layers = [
  {
    n: "L4",
    key: "interface",
    icon: MonitorPlay,
    title: "Interface Layer",
    tag: "Command centers, dashboards, copilots",
    body: "Unified command center, executive dashboards, generative AI copilots, and mobile-ready operational interfaces — the human-facing surface of the intelligent enterprise.",
    items: ["Unified command center", "Executive dashboards", "AI copilots", "Mobile operations UX"],
    tint: "bg-rose-50",
    accent: "bg-rose-500",
    text: "text-rose-700",
    border: "border-rose-500",
  },
  {
    n: "L3",
    key: "core",
    icon: Broadcast,
    title: "Core Enterprise Apps",
    tag: "Enterprise workflows & industry suites",
    body: "AI Manufacturing Suite, AI Mining Platform, Smart Facility Platform, AI Procurement Portal, and Intelligent Analytics — industry-tuned applications sitting on top of the engine.",
    items: [
      "AI Manufacturing Suite",
      "AI Mining Platform",
      "Smart Facility Platform",
      "AI Procurement Portal",
      "Intelligent Analytics Platform",
    ],
    tint: "bg-violet-50",
    accent: "bg-violet-600",
    text: "text-violet-700",
    border: "border-violet-500",
  },
  {
    n: "L2",
    key: "engine",
    icon: Cpu,
    title: "AI Engine",
    tag: "Intelligence & automation core",
    body: "Predictive analytics, computer vision, NLP, generative AI, root-cause analysis, and autonomous decision support — the reasoning core of the stack.",
    items: [
      "Predictive analytics & maintenance",
      "Computer vision & video analytics",
      "NLP & document intelligence",
      "Generative AI",
      "Root-cause & autonomous decisions",
    ],
    tint: "bg-emerald-50",
    accent: "bg-emerald-600",
    text: "text-emerald-700",
    border: "border-emerald-500",
  },
  {
    n: "L1",
    key: "foundation",
    icon: Cloud,
    title: "Cloud & Data Foundation",
    tag: "Scalable, secure, cloud-native backbone",
    body: "Cloud migration, hybrid cloud architecture, data lakes, modern data platforms, and managed cloud services — the technical foundation everything else rests on.",
    items: [
      "Cloud migration & hybrid architecture",
      "Scalable infrastructure",
      "Data lakes & modern data platform",
      "Managed cloud services",
    ],
    tint: "bg-blue-50",
    accent: "bg-blue-600",
    text: "text-blue-700",
    border: "border-blue-500",
  },
];

export default function Architecture() {
  return (
    <div data-testid="page-architecture">
      {/* HERO */}
      <section className="border-b border-zinc-200" data-testid="architecture-hero">
        <div className="container-x mx-auto py-20 lg:py-28">
          <div className="text-overline mb-6">The Bizlem Ecosystem</div>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95] max-w-5xl">
            The Intelligent Architecture Stack.
          </h1>
          <p className="mt-8 text-xl leading-relaxed text-zinc-600 max-w-3xl">
            A fully integrated, end-to-end transformation stack. Four layers — foundation, engine,
            core, interface — designed to work together, from data to decision.
          </p>
        </div>
      </section>

      {/* STACK VISUAL */}
      <section className="border-b border-zinc-200 bg-zinc-50" data-testid="stack-visual">
        <div className="container-x mx-auto py-20 lg:py-28">
          <div className="max-w-4xl mx-auto space-y-2">
            {layers.map((l, i) => (
              <div
                key={l.n}
                data-testid={`architecture-layer-${l.key}`}
                className={`${l.tint} border-2 ${l.border} p-6 lg:p-8 grid grid-cols-1 lg:grid-cols-12 gap-6 items-start hover:-translate-y-1 hover:shadow-xl transition-all duration-300 shadow-sm`}
                style={{ marginLeft: `${i * 16}px`, marginRight: `${(layers.length - 1 - i) * 16}px` }}
              >
                <div className="lg:col-span-3 flex items-center gap-4">
                  <div className={`w-16 h-16 ${l.accent} text-white flex items-center justify-center font-black text-xl shadow-md`} style={{ fontFamily: "Chivo" }}>
                    {l.n}
                  </div>
                  <l.icon size={32} weight="light" className={l.text} />
                </div>
                <div className="lg:col-span-9">
                  <div className={`text-overline ${l.text}`}>{l.tag}</div>
                  <h3 className="mt-1 text-2xl lg:text-3xl font-black tracking-tighter">{l.title}</h3>
                  <p className="mt-3 text-zinc-700 leading-relaxed">{l.body}</p>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {l.items.map((it) => (
                      <span
                        key={it}
                        className={`text-xs font-mono uppercase tracking-widest bg-white border-2 ${l.border} ${l.text} px-2.5 py-1.5 font-semibold`}
                      >
                        {it}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <p className="mt-16 text-center text-sm text-zinc-500 max-w-2xl mx-auto">
            The synthesis: unified monitoring, decision support, and continuous competitive advantage —
            one architecture, every industry.
          </p>
        </div>
      </section>

      {/* CAPABILITIES MATRIX */}
      <section className="border-b border-zinc-200" data-testid="capabilities-matrix">
        <div className="container-x mx-auto py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-12">
            <div className="lg:col-span-5">
              <div className="text-overline mb-4">Industry Intelligence Matrix</div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter">
                Heavy physical industries meet smart infrastructure services.
              </h2>
            </div>
            <p className="lg:col-span-7 lg:pt-16 text-lg text-zinc-600 max-w-2xl">
              Every layer of the stack applies across industries — from heavy metals and mining to
              agriculture, education, and smart facilities.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-200 border border-zinc-200">
            <div className="bg-white p-8 lg:p-10">
              <div className="text-overline text-zinc-500 mb-4">Heavy Physical Industries</div>
              <ul className="space-y-3 text-lg font-semibold">
                <li>Metals & Steel</li>
                <li>Mining</li>
                <li>Construction & Infrastructure</li>
                <li>Manufacturing</li>
                <li>Agriculture</li>
              </ul>
            </div>
            <div className="bg-white p-8 lg:p-10">
              <div className="text-overline text-zinc-500 mb-4">Smart Infrastructure & Services</div>
              <ul className="space-y-3 text-lg font-semibold">
                <li>Realty & Facilities Management</li>
                <li>Education</li>
                <li>Enterprise Procurement</li>
                <li>Command Center Operations</li>
                <li>Analytics & ESG</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-b border-zinc-200" data-testid="architecture-cta">
        <div className="container-x mx-auto py-20 lg:py-28">
          <div className="border border-zinc-950 p-10 lg:p-16">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
              <div className="lg:col-span-8">
                <Stack size={36} weight="light" className="text-zinc-950 mb-6" />
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter">
                  One architecture. Every industry. Real results.
                </h2>
              </div>
              <div className="lg:col-span-4 flex flex-col gap-3">
                <Link
                  to="/contact"
                  data-testid="architecture-cta-btn"
                  className="inline-flex items-center justify-between bg-zinc-950 text-white px-6 py-4 text-sm font-semibold hover:bg-[#0A33D8] transition-colors"
                >
                  Book a Consultation <ArrowUpRight size={16} weight="bold" />
                </Link>
                <Link
                  to="/platforms"
                  data-testid="architecture-platforms-btn"
                  className="inline-flex items-center justify-between border border-zinc-950 px-6 py-4 text-sm font-semibold hover:bg-zinc-950 hover:text-white transition-colors"
                >
                  View Platforms
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
